const MISSING_RATINGS = new Set(["", "-", "—", "NR", "N/R", "UNRATED"]);

const LONG_TERM_MOODYS = {
  AAA: "AAA",
  AA1: "AA+", AA2: "AA", AA3: "AA-",
  A1: "A+", A2: "A", A3: "A-",
  BAA1: "BBB+", BAA2: "BBB", BAA3: "BBB-",
  BA1: "BB+", BA2: "BB", BA3: "BB-",
  B1: "B+", B2: "B", B3: "B-",
  CAA1: "CCC+", CAA2: "CCC", CAA3: "CCC-",
  CA: "CC", C: "C",
};

const LONG_TERM_ORDER = [
  "AAA", "AA+", "AA", "AA-", "A+", "A", "A-",
  "BBB+", "BBB", "BBB-", "BB+", "BB", "BB-",
  "B+", "B", "B-", "CCC+", "CCC", "CCC-", "CC", "C", "D",
];

const SHORT_TERM_ORDER = ["A-1+", "A-1", "A-2", "A-3", "Below A-3"];
const FUND_ORDER = ["AAA", "AA", "A", "BBB", "Below BBB"];

export const CREDIT_SUMMARY_RATING_ORDER = [
  "AAA",
  "AA",
  "A",
  "BBB",
  "Below BBB",
  "A-1+",
  "A-1",
  "A-2",
  "A-3",
  "Below A-3",
  "SD",
  "NR",
];

function clean(value) {
  return String(value ?? "").trim().toUpperCase().replace(/\s+/g, " ");
}

export function isMissingRating(value) {
  return MISSING_RATINGS.has(clean(value));
}

export function normalizeLongTermRating(value) {
  const rating = clean(value).replace(/\s/g, "");
  if (MISSING_RATINGS.has(rating)) return "";
  if (LONG_TERM_MOODYS[rating]) return LONG_TERM_MOODYS[rating];
  return LONG_TERM_ORDER.includes(rating) ? rating : "";
}

export function collapseLongTermRating(value) {
  const rating = normalizeLongTermRating(value);
  if (!rating) return "NR";
  if (rating === "AAA") return "AAA";
  if (rating.startsWith("AA")) return "AA";
  if (rating.startsWith("A")) return "A";
  if (rating.startsWith("BBB")) return "BBB";
  return "Below BBB";
}

export function normalizeShortTermRating(value) {
  const rating = clean(value).replace(/\s/g, "");
  if (MISSING_RATINGS.has(rating)) return "";
  if (["A-1+", "F1+", "SP-1+"].includes(rating)) return "A-1+";
  if (["P-1", "A-1", "F1", "MIG1", "VMIG1", "SP-1"].includes(rating)) return "A-1";
  if (["P-2", "A-2", "F2", "MIG2", "VMIG2", "SP-2"].includes(rating)) return "A-2";
  if (["P-3", "A-3", "F3", "MIG3", "VMIG3", "SP-3"].includes(rating)) return "A-3";
  if (["NP", "SG", "B", "B-1", "B-2", "B-3", "C", "D", "RD"].includes(rating)) {
    return "Below A-3";
  }
  return "";
}

export function normalizeFundRating(value) {
  const rating = clean(value).replace(/\s/g, "");
  if (MISSING_RATINGS.has(rating)) return "";
  if (["AAA", "AAA-MF", "AAAM", "AAAF", "AAAMMF"].includes(rating)) return "AAA";
  if (["AA", "AA-MF", "AAF", "AAMMF"].includes(rating)) return "AA";
  if (["A", "A-MF", "AF", "AMMF"].includes(rating)) return "A";
  if (["BBB", "BAA-MF", "BBBF", "BBBMMF"].includes(rating)) return "BBB";
  if (/^(BB|B|CCC|CC|C|D)/.test(rating)) return "Below BBB";
  return "";
}

function greatest(values, order) {
  const available = values.filter(Boolean);
  if (!available.length) return "";
  return available.sort((a, b) => order.indexOf(a) - order.indexOf(b))[0];
}

export function isShortTermRating(value) {
  return Boolean(normalizeShortTermRating(value));
}

export function getRatingSection(row = {}) {
  const investmentType = clean(row.investment_type);
  const securityType = clean(row.security_type);
  const rollupRating = clean(row.rating_rollup);
  const agencyRatings = [row.rating_moody, row.rating_sp, row.rating_fitch];

  if (rollupRating === "SD" || investmentType === "CASH") {
    return "secured_deposit";
  }
  if (
    row.insured_flag === true &&
    ["CD", "BANK DEPOSIT"].includes(investmentType)
  ) {
    return "secured_deposit";
  }
  if (investmentType === "LGIP" || investmentType.includes("MONEY MARKET")) {
    return "fund_pool";
  }
  if (
    investmentType === "COMMERCIAL PAPER" ||
    securityType === "CP" ||
    securityType.includes("COMMERCIAL PAPER") ||
    agencyRatings.some(isShortTermRating)
  ) {
    return "short_term";
  }
  return "long_term";
}

export function resolveReportedRating(row = {}) {
  const section = getRatingSection(row);
  if (section === "secured_deposit") {
    return { section, normalizedRating: "SD", source: "classification" };
  }

  const normalizer = section === "short_term"
    ? normalizeShortTermRating
    : section === "fund_pool"
    ? normalizeFundRating
    : normalizeLongTermRating;
  const order = section === "short_term"
    ? SHORT_TERM_ORDER
    : section === "fund_pool"
    ? FUND_ORDER
    : LONG_TERM_ORDER;
  const rollup = normalizer(row.rating_rollup);
  if (rollup) return { section, normalizedRating: rollup, source: "rollup" };

  const agencyRating = greatest(
    [row.rating_moody, row.rating_sp, row.rating_fitch].map(normalizer),
    order
  );
  return {
    section: agencyRating ? section : "unrated",
    normalizedRating: agencyRating || "NR",
    source: agencyRating ? "agency_fallback" : "unrated",
  };
}

export const RATING_SECTION_LABELS = {
  long_term: "Long-Term",
  short_term: "Short-Term",
  fund_pool: "Short-Term Funds",
  secured_deposit: "Secured Deposits",
  unrated: "Unrated",
};

export const RATING_SECTION_ORDER = [
  "long_term",
  "short_term",
  "fund_pool",
  "secured_deposit",
  "unrated",
];
